// Lista com a ordem automática e as seções: Atrasadas · Hoje · Amanhã · Próximos dias ·
// Para depois (P0.2 / ASM-007). A tarefa atual fica no cartão de cima (CurrentTask).
// Arrastar e soltar (US-007): mouse, toque longo no celular e teclado (espaço + setas).
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DROP_SECTIONS, planDrop, resolveAskDate, sectionOfTask, type DropPlan } from '../domain/manualOrder';
import { SECTION_ORDER, buildBoard, sectionOf, type SectionKey } from '../domain/ordering';
import type { Task } from '../domain/task';
import { useAppState, useStore } from '../state/AppStore';
import { useNow } from '../state/useNow';
import { DropDateDialog } from './DropDateDialog';
import { EmptyState } from './EmptyState';
import { SectionTrash } from './SectionTrash';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';
import { TaskRow } from './TaskRow';

const SECTION_LABEL: Record<SectionKey, string> = {
  overdue: 'sections.overdue',
  today: 'sections.today',
  tomorrow: 'sections.tomorrow',
  next7: 'sections.next7',
  later: 'sections.later',
};

/**
 * Mouse e toque: vale o que está debaixo do ponteiro (uma tarefa antes da seção que a
 * contém). Teclado (sem ponteiro): o centro mais próximo.
 */
export const dropCollision: CollisionDetection = (args) => {
  const under = pointerWithin(args);
  if (under.length) {
    const tasksUnder = under.filter((c) => !String(c.id).startsWith('section:'));
    return tasksUnder.length ? tasksUnder : under;
  }
  return args.pointerCoordinates ? [] : closestCenter(args);
};

export interface DragData {
  section: SectionKey;
  index: number;
}

function SectionBlock({
  sectionKey,
  tasks,
  startIndex,
  now,
  nextId,
  canEdit,
  dragging,
  dragFrom,
}: {
  sectionKey: SectionKey;
  tasks: Task[];
  startIndex: number;
  now: number;
  nextId: string | null;
  canEdit: boolean;
  dragging: boolean;
  dragFrom: SectionKey | null;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const { setNodeRef, isOver } = useDroppable({ id: `section:${sectionKey}`, data: { section: sectionKey, index: tasks.length } satisfies DragData });
  const refused = dragging && sectionKey === 'overdue' && dragFrom !== 'overdue';
  return (
    <section
      ref={setNodeRef}
      className={cls('section', `section--${sectionKey}`, dragging && isOver && !refused && 'section--over', refused && 'section--refuse')}
      aria-labelledby={`sec-${sectionKey}`}
      data-section={sectionKey}
    >
      <div className="section__bar">
        <h3 className="section__head" id={`sec-${sectionKey}`}>
          {sectionKey === 'overdue' && <Icon name="alert" size={15} />}
          <span>{t(SECTION_LABEL[sectionKey])}</span>
          <span className="section__count">{tasks.length}</span>
        </h3>
        {/* apaga todas as tarefas da seção, depois de perguntar (AC-235) */}
        {tasks.length > 0 && (
          <SectionTrash
            label={t('sections.deleteAll', { section: t(SECTION_LABEL[sectionKey]) })}
            title={t('sections.deleteAllTitle', { count: tasks.length, section: t(SECTION_LABEL[sectionKey]) })}
            body={t('sections.deleteAllBody')}
            disabled={!canEdit}
            onConfirm={() => store.deleteTasks(tasks.map((x) => x.id))}
          />
        )}
      </div>
      <SortableContext items={tasks.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <ul className="section__list">
          {tasks.map((task, i) => (
            <TaskRow
              key={task.id}
              task={task}
              index={startIndex + i + 1}
              now={now}
              isNext={nextId === task.id}
              canEdit={canEdit}
              drag={{ section: sectionKey, index: i }}
            />
          ))}
        </ul>
      </SortableContext>
      {dragging && tasks.length === 0 && <div className="section__drop">{t('drag.dropHere')}</div>}
    </section>
  );
}

export function TaskList({ canEdit }: { canEdit: boolean }) {
  const store = useStore();
  const tasks = useAppState((s) => s.tasks);
  const timeFormat = useAppState((s) => s.profile?.time_format ?? '24h');
  const now = useNow();
  const board = buildBoard(tasks, new Date(now));
  const [dragId, setDragId] = useState<string | null>(null);
  const [ask, setAsk] = useState<Extract<DropPlan, { kind: 'askDate' }> | null>(null);
  // As seções vazias aparecem ao arrastar e somem ao soltar: a página rola a mesma
  // diferença para a tarefa não sair de baixo do cursor (sem "pulo").
  const anchor = useRef<{ id: string; top: number } | null>(null);
  const rowTop = (id: string) => document.querySelector(`[data-task-id="${id}"]`)?.getBoundingClientRect().top ?? null;
  const remember = (id: string) => {
    const top = rowTop(id);
    anchor.current = top === null ? null : { id, top };
  };
  useLayoutEffect(() => {
    const a = anchor.current;
    if (!a) return;
    anchor.current = null;
    const top = rowTop(a.id);
    if (top !== null && Math.abs(top - a.top) > 1) window.scrollBy(0, top - a.top);
  });
  const sensors = useSensors(
    // mouse: precisa mexer 6 px — um clique simples continua sendo clique
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // celular: segurar o dedo 250 ms (sem atrapalhar a rolagem)
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (tasks.length === 0) return <EmptyState />;

  const byKey = new Map(board.sections.map((s) => [s.key, s.tasks]));
  const dragging = dragId !== null;
  const visible = SECTION_ORDER.filter((k) => byKey.has(k) || (dragging && DROP_SECTIONS.includes(k)));
  const dragFrom = dragId ? sectionOfTask(board, dragId) : null;
  const dragTask = dragId ? board.sections.flatMap((s) => s.tasks).find((x) => x.id === dragId) ?? null : null;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    remember(String(active.id));
    setDragId(null);
    const target = over?.data.current as DragData | undefined;
    if (!target) return;
    const plan = planDrop(board, String(active.id), target.section, target.index, new Date(now));
    if (plan.kind === 'reorder') void store.moveTask({ taskId: String(active.id), ids: plan.ids });
    if (plan.kind === 'move') void store.moveTask({ taskId: plan.taskId, patch: plan.patch, ids: plan.ids });
    if (plan.kind === 'askDate') setAsk(plan);
  };

  let n = 0;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={dropCollision}
      // as seções vazias aparecem ao começar o arraste e empurram a lista: mede sempre
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={({ active }) => {
        remember(String(active.id));
        setDragId(String(active.id));
      }}
      onDragCancel={({ active }) => {
        remember(String(active.id));
        setDragId(null);
      }}
      onDragEnd={onDragEnd}
    >
      <div className={cls('sections', dragging && 'sections--dragging')}>
        {visible.map((key) => {
          const list = byKey.get(key) ?? [];
          const start = n;
          n += list.length;
          return (
            <SectionBlock
              key={key}
              sectionKey={key}
              tasks={list}
              startIndex={start}
              now={now}
              nextId={!board.current ? board.next?.id ?? null : null}
              canEdit={canEdit}
              dragging={dragging}
              dragFrom={dragFrom}
            />
          );
        })}
      </div>
      <DragOverlay>
        {dragTask && (
          <div className="task task--overlay">
            <div className="task__top">
              <span className="task__title">{dragTask.title}</span>
            </div>
          </div>
        )}
      </DragOverlay>
      {ask && (
        <DropDateDialog
          task={board.sections.flatMap((s) => s.tasks).find((x) => x.id === ask.taskId)!}
          section={ask.section}
          timeFormat={timeFormat}
          onCancel={() => setAsk(null)}
          onConfirm={(date, time) => {
            const r = resolveAskDate(board, ask, date, time, (d, tm) => sectionOf({ due_date: d, due_time: tm, status: 'todo' }, new Date(now)));
            setAsk(null);
            void store.moveTask({ taskId: ask.taskId, patch: r.patch, ids: r.ids });
          }}
        />
      )}
    </DndContext>
  );
}
